/**
 * StopScreen.tsx — the "not suitable" screen + free callback (CLIN-02 / R31).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: every dead-end ends the same way — a clear "not suitable right now", the
 * FREE callback form INLINE (a stop is a lead, never a dead end), and, when the
 * rule is mental-health-flagged (R31), the crisis signposting rendered
 * PROMINENTLY above everything else. The server's vetted lines
 * (`signposting.lines`) render verbatim; if an mhRisk result arrives without
 * them, the local {@link CRISIS_LINES} fail-safe renders instead — an mhRisk
 * stop can NEVER appear without 999 / Samaritans 116 123 / NHS 111. This same
 * screen is the CLIN-02 destination for a `not_eligible` submit result, so a
 * "not suitable" outcome is never dressed up as good news.
 *
 * TWO OUTCOME PARAGRAPHS, not one (2026-08-18). Every stop used to read "this
 * treatment wouldn't be safe for you at the moment", which is simply untrue of a
 * BMI stop: that patient is OUTSIDE THE RANGE THE TREATMENT IS LICENSED FOR, and
 * nothing in their answers said anything about them is unsafe. Telling a healthy
 * person a medicine is unsafe for them is a clinical inaccuracy, not a wording
 * nit — and because the old copy also named no blocker at all, a stakeholder
 * walking the live journey could not work out what had stopped him. See
 * {@link isThresholdStop} for why the two are reliably distinguishable.
 */
import { Button, Card, HeartIcon } from '../ui/kit';
import { CallbackForm } from './CallbackForm';
import { CRISIS_LINES } from './signposting';
import { JourneyScreen } from './JourneyScreen';
import type { Attribution, EvaluationResult } from '../api/types';

/**
 * Build a synthetic hard-stop result for the `not_eligible` submit branch, so
 * the assessment result screen can route a "not eligible" verdict straight
 * here rather than to a positive screen (CLIN-02).
 */
export function notEligibleResult(mhRisk = false): EvaluationResult {
  return { outcome: 'hard_stop', mhRisk, offerCallback: true, flags: [] };
}

/**
 * Is this stop a LICENSED-THRESHOLD decision (the patient's BMI sits outside the
 * band the treatment is licensed in) rather than a clinical-safety one?
 *
 * `bmiThreshold` is the signal, and it is a STRUCTURAL one rather than a guess
 * from the stop's name. The clinic engine only ever populates it from its two
 * BMI-kind rules — the absolute floor and the final gate, whose threshold moves
 * with comorbidity/ethnicity, which is exactly why the number has to come from
 * the server rather than be restated here. Every other rule kind the engine
 * implements (an age minimum, an option stop such as pregnancy or a Q6 safety
 * condition) reaches the same `hard_stop` outcome carrying NO threshold, so the
 * absence of the field is as meaningful as its presence.
 *
 * Deliberately NOT keyed on `stopKey`: the key is seed configuration
 * (`config.stopKey`), so a re-seed could rename it and silently flip a patient
 * back to being told a medicine is unsafe for them. The synthetic `not_eligible`
 * result minted by {@link notEligibleResult} carries neither field and correctly
 * falls to the clinical-safety wording, which is the honest thing to say when
 * the verdict came from the whole questionnaire and is not attributable.
 */
export function isThresholdStop(result: EvaluationResult): boolean {
  return result.bmiThreshold !== undefined;
}

/** Hard-stop screen: outcome copy, R31 signposting, inline free callback. */
export function StopScreen({
  result,
  clinicSlug,
  attribution,
  leadId,
  staffView = false,
  onRestart,
  restartLabel = 'Back to start',
}: {
  result: EvaluationResult;
  clinicSlug: string;
  attribution?: Attribution;
  /** The step-0 lead, when the journey already created one — links inline callbacks to the CRM deal. */
  leadId?: string;
  /**
   * True when a STAFF member is filling the form on a patient's behalf
   * (AdminFormPage): the outcome copy and R31 signposting render exactly as
   * the patient would see them, but the inline CallbackForm is replaced with
   * a plain instruction — nesting a callback request inside the admin console
   * would file a ticket back at the very staff member on the call.
   */
  staffView?: boolean;
  /**
   * Fired by the button below. Callers decide how far "back" actually goes —
   * QuestionFlow's in-flow hard stop rewinds to the exact question that
   * triggered it (the assessment is still a mutable draft); JourneyPage's
   * post-submit stop cannot reopen an immutable submitted assessment, so it
   * jumps back to Projection (Capture's already-known answers are reused,
   * never re-asked) instead of a full restart.
   */
  onRestart?: () => void;
  /** Button copy — defaults to a full restart's wording; callers whose
   * `onRestart` does NOT return to the very beginning should override this
   * (e.g. "Back") so the label never promises more than it does. */
  restartLabel?: string;
}) {
  const signpostingLines =
    result.signposting && result.signposting.lines.length > 0
      ? result.signposting.lines
      : result.mhRisk
        ? [...CRISIS_LINES]
        : null;
  const threshold = isThresholdStop(result);

  return (
    <JourneyScreen>
      <div style={{ textAlign: 'center' }}>
        <div
          aria-hidden="true"
          style={{
            width: '64px',
            height: '64px',
            borderRadius: 'var(--gps-r-pill)',
            background: 'var(--gps-warn-bg)',
            color: 'var(--gps-warn-text)',
            display: 'grid',
            placeItems: 'center',
            fontSize: '28px',
            margin: '6px auto 14px',
          }}
        >
          <HeartIcon size={28} />
        </div>
        <h1 style={{ fontSize: '23px', margin: '0 0 8px', color: 'var(--gps-text)' }}>
          This treatment isn&rsquo;t right for you just now
        </h1>
        {threshold ? (
          <>
            <p style={{ color: 'var(--gps-muted)', fontSize: '14.5px', margin: '0 0 4px' }}>
              This treatment is licensed for people whose BMI is at or above a set level, and the
              height and weight you gave put you below that level — so we can&rsquo;t prescribe it
              for you today. That is where the licence sits. It is not a judgement about you, and
              nothing in your answers says anything about you is unsafe.
            </p>
            {result.bmi !== undefined ? (
              <p style={{ color: 'var(--gps-text)', fontSize: '14.5px', fontWeight: 700, margin: '10px 0 4px' }}>
                Your BMI from the height and weight you gave is {result.bmi.toFixed(1)}. The level
                for this treatment is {result.bmiThreshold?.toFixed(1)}.
              </p>
            ) : null}
            <p style={{ color: 'var(--gps-muted)', fontSize: '14.5px', margin: '0 0 4px' }}>
              A clinician can still talk it through with you on a free callback — including what
              else may be worth looking at.
            </p>
          </>
        ) : (
          <p style={{ color: 'var(--gps-muted)', fontSize: '14.5px', margin: '0 0 4px' }}>
            Based on your answers, this treatment wouldn&rsquo;t be safe for you at the moment.
            That&rsquo;s a safety decision, not a judgement — and you&rsquo;re not on your own. A
            clinician can talk it through.
          </p>
        )}
      </div>

      {signpostingLines ? (
        <div
          role="alert"
          style={{
            textAlign: 'left',
            background: 'var(--gps-surface-2)',
            border: '1px solid var(--gps-border)',
            borderLeft: '4px solid var(--gps-cta)',
            borderRadius: 'var(--gps-r-md)',
            padding: '14px 16px',
            margin: 'var(--gps-s-4) 0',
          }}
        >
          <h2 style={{ margin: '0 0 8px', fontFamily: 'var(--gps-font-body)', fontSize: '14px', fontWeight: 700, color: 'var(--gps-text)' }}>
            If you need support now
          </h2>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '14px', color: 'var(--gps-text)' }}>
            {signpostingLines.map((line) => (
              <li key={line} style={{ margin: '5px 0' }}>
                {line}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Card style={{ marginTop: 'var(--gps-s-4)' }}>
        {staffView ? (
          <p style={{ color: 'var(--gps-text)', fontSize: '14.5px', margin: 0 }}>
            Advise the caller per the signposting above.
          </p>
        ) : (
          <>
            <h2 style={{ fontFamily: 'var(--gps-font-display)', color: 'var(--gps-green-text)', marginTop: 0, fontSize: '20px' }}>
              Request a free callback
            </h2>
            <p style={{ color: 'var(--gps-muted)', fontSize: '14px' }}>
              Our team can talk through what your answers mean and what your options are — free, and with
              a real person.
            </p>
            <CallbackForm clinicSlug={clinicSlug} attribution={attribution} leadId={leadId} />
          </>
        )}
      </Card>

      {onRestart ? (
        <div style={{ marginTop: 'var(--gps-s-3)' }}>
          <Button type="button" variant="ghost" onClick={onRestart}>
            {restartLabel}
          </Button>
        </div>
      ) : null}
    </JourneyScreen>
  );
}
